"use client";

import { useState } from "react";
import { ArrowUpRight, Briefcase, Check, DollarSign, GitBranch, GraduationCap, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MissionAndVision() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [hoveredLeft, setHoveredLeft] = useState<number | null>(null);
  const [hoveredRight, setHoveredRight] = useState<number | null>(null);
  const talentCards = [{
    icon: Briefcase,
    title: "Business Process Consulting",
    description: "We review existing workflows and help businesses create clearer, more efficient, and better-controlled processes.",
    benefits: ["REQUIREMENTS ANALYSIS", "Operational efficiency", "Growth strategy"]
  }, {
    icon: DollarSign,
    title: "Financial Optimization",
    description: "Use accurate data and practical analysis to improve financial visibility, control costs, and support stronger business decisions.",
    benefits: ["Cost reduction", "REVENUE FORECASTING", "Capital allocation"]
  }, {
    icon: GraduationCap,
    title: "Tailored Business Solutions",
    description: "We design practical solutions around your business requirements, systems, processes, and long-term objectives.",
    benefits: ["Leadership development", "Change management", "IMPLEMENTATION PLANNING"]
  }];
  const employerCards = [{
    icon: Target,
    title: "Data Analytics",
    description: "Transform complex information into clear reports, dashboards, performance metrics, and actionable business insights.",
    benefits: ["DATA INTEGRATION", "Predictive modeling", "Performance metrics"]
  }, {
    icon: Zap,
    title: "Process Automation",
    description: "Reduce repetitive tasks, manual data entry, and reporting errors through reliable workflow and process automation.",
    benefits: ["Workflow integration", "SYSTEM CONNECTIVITY", "Efficiency scaling"]
  }, {
    icon: GitBranch,
    title: "Data & System Integration",
    description: "Connect data from different files, platforms, and systems to create consistent, accessible, and scalable information flows.",
    benefits: ["System architecture", "Scalability planning", "SCALABLE ARCHITECTURE"]
  }];
  return <section data-section-id="744" id="mission-and-vision" className="text-foreground relative overflow-hidden py-24 md:py-32 lg:py-[140px] px-4 sm:px-6 lg:px-8 dark bg-accent">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          
          <h2 className="font-default font-semibold text-4xl sm:text-5xl md:text-[56px] leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Precision Strategy.{" "}
            <span style={{
            color: "var(--secondary)"
          }}>Actionable Intelligence.</span>
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
            Infinix Solutions delivers high-impact consulting built for the complexity of modern digital enterprises.
          </p>
        </div>

        {}
        <div ref={gridRef} className="relative">
          {}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 pointer-events-none z-10">
            <div className={`relative w-px h-full transition-all duration-1000 ${gridVisible ? "opacity-100" : "opacity-0"}`} style={{
            background: "linear-gradient(to bottom, transparent 0%, var(--secondary) 15%, var(--secondary) 85%, transparent 100%)"
          }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="px-3 py-1.5 bg-background border rounded-lg font-mono text-xs uppercase tracking-wider whitespace-nowrap" style={{
                color: "var(--secondary)",
                borderColor: "var(--secondary)",
                transform: "rotate(-90deg)"
              }}>
                  ↔ SYNERGY
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {}
            <div className={`transition-all duration-700 delay-100 ease-out ${gridVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
              <div className="flex items-center gap-3 mb-8">
                <div className="h-px flex-1 max-w-[40px]" style={{
                backgroundColor: "var(--secondary)"
              }} aria-hidden="true" />
                <h3 className="font-mono text-sm uppercase tracking-[0.2em] font-medium" style={{
                color: "var(--secondary)"
              }}>BUSINESS SOLUTIONS</h3>
              </div>

              <div className="space-y-5">
                {talentCards.map((card, i) => {
                const Icon = card.icon;
                const isHovered = hoveredLeft === i;
                return <div key={card.title} data-index={i} onMouseEnter={() => setHoveredLeft(i)} onMouseLeave={() => setHoveredLeft(null)} className="group relative bg-card border rounded-lg p-8 transition-all duration-300 cursor-pointer" style={{
                  borderColor: isHovered ? "var(--secondary)" : "var(--border)",
                  boxShadow: isHovered ? "0 0 0 1px var(--secondary), 0 0 40px -10px var(--secondary)" : "var(--shadow-sm)"
                }}>
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center justify-center w-11 h-11 rounded-lg border transition-colors duration-300" style={{
                      borderColor: isHovered ? "var(--secondary)" : "var(--border)",
                      backgroundColor: isHovered ? "var(--accent)" : "transparent"
                    }}>
                          <Icon className="w-5 h-5 transition-colors duration-300" strokeWidth={1.5} style={{
                        color: "var(--secondary)"
                      }} />
                        </div>
                        <ArrowUpRight className={`w-5 h-5 transition-all duration-300 ${isHovered ? "opacity-100 translate-x-0 -translate-y-0" : "opacity-0 -translate-x-2 translate-y-2"}`} style={{
                      color: "var(--secondary)"
                    }} strokeWidth={1.5} />
                      </div>

                      <h4 className="font-default font-semibold text-[22px] leading-tight mb-3 text-card-foreground">
                        {card.title}
                      </h4>
                      <p className="font-default text-[15px] leading-relaxed text-muted-foreground">
                        {card.description}
                      </p>

                      <div className="overflow-hidden transition-all duration-500 ease-out" style={{
                    maxHeight: isHovered ? "200px" : "0px",
                    opacity: isHovered ? 1 : 0,
                    marginTop: isHovered ? "20px" : "0px"
                  }}>
                        <div className="pt-5 border-t space-y-2.5" style={{
                      borderColor: "var(--border)"
                    }}>
                          {card.benefits.map((benefit, j) => <div key={benefit} data-index={j} className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-wider">
                              <Check className="w-3.5 h-3.5 shrink-0" style={{
                          color: "var(--secondary)"
                        }} strokeWidth={2} />
                              <span className="text-muted-foreground">{benefit}</span>
                            </div>)}
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-8">
                <Button variant="outline" size="lg" className="bg-transparent border-border hover:bg-accent hover:text-accent-foreground font-mono text-xs uppercase tracking-widest rounded-lg" asChild>
                  <Link to="/WhoWeArePage">Learn about us</Link>
                </Button>
              </div>
            </div>

            {}
            <div className={`transition-all duration-700 delay-200 ease-out ${gridVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
              <div className="flex items-center gap-3 mb-8">
                <div className="h-px flex-1 max-w-[40px]" style={{
                backgroundColor: "var(--secondary)"
              }} aria-hidden="true" />
                <h3 className="font-mono text-sm uppercase tracking-[0.2em] font-medium" style={{
                color: "var(--secondary)"
              }}>AUTOMATION &amp; DATA INTELLIGENCE</h3>
              </div>

              <div className="space-y-5">
                {employerCards.map((card, i) => {
                const Icon = card.icon;
                const isHovered = hoveredRight === i;
                return <div key={card.title} data-index={i} onMouseEnter={() => setHoveredRight(i)} onMouseLeave={() => setHoveredRight(null)} className="group relative bg-card border rounded-lg p-8 transition-all duration-300 cursor-pointer" style={{
                  borderColor: isHovered ? "var(--secondary)" : "var(--border)",
                  boxShadow: isHovered ? "0 0 0 1px var(--secondary), 0 0 40px -10px var(--secondary)" : "var(--shadow-sm)"
                }}>
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center justify-center w-11 h-11 rounded-lg border transition-colors duration-300" style={{
                      borderColor: isHovered ? "var(--secondary)" : "var(--border)",
                      backgroundColor: isHovered ? "var(--accent)" : "transparent"
                    }}>
                          <Icon className="w-5 h-5 transition-colors duration-300" strokeWidth={1.5} style={{
                        color: "var(--secondary)"
                      }} />
                        </div>
                        <ArrowUpRight className={`w-5 h-5 transition-all duration-300 ${isHovered ? "opacity-100 translate-x-0 -translate-y-0" : "opacity-0 -translate-x-2 translate-y-2"}`} style={{
                      color: "var(--secondary)"
                    }} strokeWidth={1.5} />
                      </div>

                      <h4 className="font-default font-semibold text-[22px] leading-tight mb-3 text-card-foreground">
                        {card.title}
                      </h4>
                      <p className="font-default text-[15px] leading-relaxed text-muted-foreground">
                        {card.description}
                      </p>

                      <div className="overflow-hidden transition-all duration-500 ease-out" style={{
                    maxHeight: isHovered ? "200px" : "0px",
                    opacity: isHovered ? 1 : 0,
                    marginTop: isHovered ? "20px" : "0px"
                  }}>
                        <div className="pt-5 border-t space-y-2.5" style={{
                      borderColor: "var(--border)"
                    }}>
                          {card.benefits.map((benefit, j) => <div key={benefit} data-index={j} className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-wider">
                              <Check className="w-3.5 h-3.5 shrink-0" style={{
                          color: "var(--secondary)"
                        }} strokeWidth={2} />
                              <span className="text-muted-foreground">{benefit}</span>
                            </div>)}
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-8">
                <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono text-xs uppercase tracking-widest rounded-lg" asChild>
                  <Link to="/OurServicesPage">View our services</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:hidden flex items-center justify-center gap-4 my-12">
            <div className="h-px flex-1" style={{
            background: "linear-gradient(to right, transparent, var(--secondary), transparent)"
          }} aria-hidden="true" />
            <div className="px-3 py-1.5 bg-background border rounded-lg font-mono text-xs uppercase tracking-wider" style={{
            color: "var(--secondary)",
            borderColor: "var(--secondary)"
          }}>
              ↔ SYNERGY
            </div>
            <div className="h-px flex-1" style={{
            background: "linear-gradient(to right, transparent, var(--secondary), transparent)"
          }} aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>;
}